import React, { useState, useEffect, useRef, Suspense, useCallback } from 'react';
import mammoth from 'mammoth';
import { Box, Typography, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PropTypes from "prop-types";
import { ConstantLoaderModal } from "../../../Modals/LoaderModal";
import DescriptionIcon from '@mui/icons-material/Description';
import Utility from "../../../../Utility";
import TextField from "@mui/material/TextField";
import {isMobile} from "react-device-detect";

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const PAGE_PADDING_PX = 96;

const pageStyles = `
.docx-page {
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px;
  background-color: white;
  margin: 20px auto;
  padding: ${PAGE_PADDING_PX}px;
  box-shadow: 0 0 10px rgba(0,0,0,0.2);
  box-sizing: border-box;
  border-radius: 4px;
}
`;

const DOCXViewer = ({ docxBase64, initPage, closeModal }) => {
    const containerRef = useRef(null);
    const [pages, setPages] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [scale, setScale] = useState(0.75);

    const decodeDocxBase64 = (base64) => {
        const content = base64.split('base64,')[1] || base64;
        const binary = atob(content);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes.buffer;
    };
    const onChange = useCallback((e) => {
        const value = e.target.value;
        if (value === '') return setCurrentPage('');
        const page = Number(value);
        if (!isNaN(page)) setCurrentPage(page);
    }, []);
    const onBlur=() => {
        let page = Number(currentPage);
        if (!page || page < 1) page = 1;
        if (page > pages?.length ?? 0) page = pages?.length ?? 0;
        setCurrentPage(page);
    }

    const splitHtmlByHeight = (html) => {
        const temp = document.createElement('div');
        temp.style.position = 'absolute';
        temp.style.visibility = 'hidden';
        temp.style.width = `${A4_WIDTH_PX}px`;
        temp.style.padding = `${PAGE_PADDING_PX}px`;
        temp.innerHTML = html;

        document.body.appendChild(temp);

        const result = [];
        let page = document.createElement('div');
        let height = 0;

        [...temp.children].forEach(node => {
            const clone = node.cloneNode(true);
            temp.appendChild(clone);
            const nodeHeight = clone.offsetHeight;

            if (height + nodeHeight > A4_HEIGHT_PX) {
                result.push(page.innerHTML);
                page = document.createElement('div');
                height = 0;
            }

            page.appendChild(clone);
            height += nodeHeight;
        });

        if (page.innerHTML) result.push(page.innerHTML);

        document.body.removeChild(temp);
        return result;
    };

    const loadDocx = async () => {
        try {
            const arrayBuffer = decodeDocxBase64(docxBase64);
            const { value } = await mammoth.convertToHtml({ arrayBuffer });
            const pageContents = splitHtmlByHeight(value);

            setPages(pageContents);
            if (initPage > 0 && initPage <= pageContents.length) setCurrentPage(initPage);
        } catch (err) {
            console.error('DOCX load error:', err);
        }
    };

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = pageStyles;
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    useEffect(() => {
        loadDocx();
    }, [docxBase64, initPage]);

    useEffect(() => {
        if (!containerRef.current || !pages[currentPage - 1]) return;

        containerRef.current.innerHTML = `
          <div class="docx-page">
            ${pages[currentPage - 1]}
          </div>
        `;
        containerRef.current.style.transform = `scale(${scale})`;
        containerRef.current.style.transformOrigin = 'top center';
    }, [pages, currentPage, scale]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            onClick={() => closeModal?.()}
            sx={{
                width: '100dvw',
                maxHeight: '100dvh',
                paddingTop: !Utility.isMobile() ? '10dvh' : 0,
            }}
        >
            <Box position="sticky" top={20} zIndex={10} display="flex" flexDirection="column" alignItems="center" onClick={(e) => e.stopPropagation()}>
                <Box position="sticky" top={30} zIndex={10} display="flex">
                    <IconButton onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                        <ArrowBackIosNewIcon />
                    </IconButton>
                    <IconButton onClick={() => setCurrentPage(p => Math.min(p + 1, pages.length))} disabled={currentPage === pages.length}>
                        <ArrowForwardIosIcon />
                    </IconButton>
                        <>
                            <IconButton onClick={() => setScale(s => s + 0.25)}><ZoomInIcon /></IconButton>
                            <IconButton onClick={() => setScale(s => Math.max(s - 0.25, 0.25))}><ZoomOutIcon /></IconButton>
                        </>
                </Box>
                <div className="flex flex-row gap-1 items-center display-center">
                    <Typography fontSize="0.75rem" lineHeight="20px">Page</Typography>

                    <TextField
                        type="number"
                        value={currentPage}
                        onChange={onChange}
                        onBlur={onBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                        }}
                        InputProps={{
                            inputProps: { min: 1, max: pages?.length ?? 0, style: { textAlign: 'center', padding: 0 } },
                            sx: { height: 24, fontSize: '0.75rem' },
                        }}
                        variant="outlined"
                        size="small"
                        sx={{ width: 50 }}
                    />

                    <Typography fontSize="0.75rem" lineHeight="20px">of {pages?.length ?? 0}</Typography>
                </div>
            </Box>

            <Box
                onClick={(e) => e.stopPropagation()}
                ref={containerRef}
                zIndex={10}
                top={30}
                position="sticky"
                sx={{
                    pt: '2dvh',
                    overflowY: !isMobile ? 'auto' : '',
                    pb: '40dvh',
                }}
            />
        </Box>
    );
};

function DisplayDocx({ id, closeModal }) {
    const [docxData, setDocxData] = useState(null);
    const [initialPage, setInitialPage] = useState(1);

    useEffect(() => {
        let fileUrl = id;
        const match = id.match(/#page=(\d+)/);
        if (match) {
            setInitialPage(parseInt(match[1], 10));
            fileUrl = id.split('#')[0];
        }

        const fetchDocx = async () => {
            try {
                const base64 = await Utility.fetchFileAsBase64(fileUrl);
                setDocxData(base64);
            } catch (error) {
                console.error('Failed to fetch DOCX file:', error);
            }
        };

        fetchDocx();
    }, [id]);

    if (!docxData) return <DescriptionIcon />;

    return (
        <Suspense fallback={<ConstantLoaderModal />}>
            <DOCXViewer docxBase64={docxData} initPage={initialPage} closeModal={() => closeModal? closeModal() : null} />
        </Suspense>
    );
}

DisplayDocx.propTypes = {
    id: PropTypes.string.isRequired
};

export default DisplayDocx;
